/**
 * Tenancy & Capability Model — Mach Five Magnet
 * Shared between the API (scope resolver, route guards) and the client (UI modes).
 *
 * Two orthogonal axes:
 *   1. CAPABILITY  — what a person can do.  Driven by org channel × member role
 *                    (this file), with platform admins (acctEntity.class) able to
 *                    act across all orgs.
 *   2. TENANCY     — whose data they can touch.  Resolved at request time from the
 *                    org tree (acctGroup.parent) + membership (acctGroupMember).
 *                    See resolveScope() contract in the tenancy_model design doc;
 *                    that lives in the API, not here.
 *
 * Schema mapping:
 *   - Org / tenant      = acctGroup        (acctGroup.class = ORG_CHANNELS value)
 *   - Person            = acctEntity       (acctEntity.class = capability tier ladder)
 *   - Membership        = acctGroupMember  (member_role = MEMBER_ROLES value)
 *   - Magnet (resource) = sseEtherSubscriberApp.owner_guid = the owning org guid
 *
 * NOTE: This module is the authoritative DEFAULT matrix. Environment-specific
 * overrides (e.g. gating a capability behind a PROD flag) belong in coreMeta and
 * are layered on top by ConfigLoader — they should never contradict the shape here.
 */

import { getRoleByName, isAdminRole } from './entityClasses.js';

/* =========================================================================
   Org channels — the value stored in acctGroup.class
   ========================================================================= */
export const ORG_CHANNELS = Object.freeze({
  reseller_org: {
    value: 'reseller_org',
    label: 'Reseller / Agency',
    desc: 'A Mach Five Marketing reseller. Creates and manages client accounts, '
        + 'provisions magnets on their behalf, monitors stats and ad numbers.',
    uiMode: 'reseller',
    billable: true,            // optionally billed separately (wholesale)
    managesSubtree: true,      // owns a subtree of managed_client orgs via acctGroup.parent
  },
  managed_client: {
    value: 'managed_client',
    label: 'Managed Client',
    desc: 'A client account created by a reseller. Read-only: views its own active '
        + 'magnets, stats, and ad numbers. Does not provision.',
    uiMode: 'managed',
    billable: true,            // platform can invoice the client DIRECTLY (value-add)
    managesSubtree: false,
    readOnly: true,
  },
  saas_org: {
    value: 'saas_org',
    label: 'Self-Serve',
    desc: 'A self-serve SaaS customer. Provisions its own magnets and connects its '
        + 'own ad accounts — the full demo experience.',
    uiMode: 'self_serve',
    billable: true,            // pays MFM directly
    managesSubtree: false,
  },
});

/* =========================================================================
   Member roles — the value stored in acctGroupMember.member_role
   (a person's role WITHIN an org, independent of their global acctEntity.class)
   ========================================================================= */
export const MEMBER_ROLES = Object.freeze({
  owner:  { value: 'owner',  rank: 30, label: 'Owner' },
  admin:  { value: 'admin',  rank: 20, label: 'Admin' },
  // A reseller-org role only (no matrix entry in other channels): a person who
  // works a SUBSET of the agency's client book. Pair with acctMemberClientScope
  // rows on the membership to restrict which managed clients they reach; the
  // capability set below is reseller-grade on those clients but carries none of
  // the agency-level powers (team, client creation, billing).
  account_manager: { value: 'account_manager', rank: 15, label: 'Account manager' },
  member: { value: 'member', rank: 10, label: 'Member' },
  viewer: { value: 'viewer', rank: 0,  label: 'Viewer' },
});

/* =========================================================================
   Capabilities — the atomic permissions the UI and API gate on
   ========================================================================= */
export const CAPABILITIES = Object.freeze({
  VIEW_DASHBOARD:        'view_dashboard',
  VIEW_MAGNETS:          'view_magnets',
  PROVISION_MAGNETS:     'provision_magnets',     // create new magnets
  MANAGE_MAGNETS:        'manage_magnets',         // edit/configure/flow-builder
  VIEW_CONVERSATIONS:    'view_conversations',
  VIEW_STATS:            'view_stats',
  VIEW_AD_METRICS:       'view_ad_metrics',
  CONNECT_AD_ACCOUNTS:   'connect_ad_accounts',
  CREATE_CLIENT_ACCOUNTS:'create_client_accounts', // reseller creating managed clients
  MANAGE_TEAM:           'manage_team',             // invite/manage org members
  MANAGE_DELIVERABLES:   'manage_deliverables',     // author/send/finalize deliverables + questionnaires
  VIEW_BILLING:          'view_billing',
  MANAGE_BILLING:        'manage_billing',
});

const C = CAPABILITIES;
const ALL_CAPABILITIES = Object.freeze(Object.values(C));
const VIEW_ONLY = Object.freeze([
  C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS,
]);

/* =========================================================================
   Read vs write — which capabilities MUTATE something.

   Needed because "platform admin" is two different privileges wearing one name:
   seeing across every org, and being allowed to change things. The role that was
   supposed to have the first without the second (admin_readonly_role) could not be
   expressed at all before this split — every gate asked "is this an admin?" and it
   answered yes, so the restriction existed only in the UI. That role has since been
   removed as unused, which makes this partition DORMANT rather than obsolete: it is
   what any future restricted admin class will be measured against, and what
   isReadOnlyAdminClass derives its answer from.

   Declared explicitly rather than inferred from the `view_` prefix: a capability
   named without that prefix (or a future `export_*`) would silently land in the
   wrong bucket, and the wrong bucket here means either a privilege hole or a
   locked-out help desk. The partition is total and disjoint, and a test asserts
   exactly that — so ADDING a capability without classifying it fails the build
   rather than defaulting to something.
   ========================================================================= */
export const WRITE_CAPABILITIES = Object.freeze([
  C.PROVISION_MAGNETS, C.MANAGE_MAGNETS, C.CONNECT_AD_ACCOUNTS,
  C.CREATE_CLIENT_ACCOUNTS, C.MANAGE_TEAM, C.MANAGE_DELIVERABLES, C.MANAGE_BILLING,
]);
export const READ_CAPABILITIES = Object.freeze([
  C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.VIEW_CONVERSATIONS,
  C.VIEW_STATS, C.VIEW_AD_METRICS, C.VIEW_BILLING,
]);

/** Does this capability change state? */
export function isWriteCapability(capability) {
  return WRITE_CAPABILITIES.includes(capability);
}

/* =========================================================================
   Default capability matrix:  channel → member_role → capabilities
   Scope (WHICH orgs/owner_guids) is applied separately by the API resolver;
   here we only say WHAT each role may do within its accessible scope.
   ========================================================================= */
export const CAPABILITY_MATRIX = Object.freeze({
  reseller_org: {
    owner: ALL_CAPABILITIES,
    admin: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS, C.CONNECT_AD_ACCOUNTS,
      C.CREATE_CLIENT_ACCOUNTS, C.MANAGE_TEAM, C.MANAGE_DELIVERABLES, C.VIEW_BILLING,
    ],
    // Reseller-grade on their (assigned) clients, none of the agency-level
    // powers. NOTE: direct membership also projects this set onto the agency
    // org itself (directCapsByOrg) — accepted, since nothing here manages the
    // org: no manage_team, no create_client_accounts, no billing.
    account_manager: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS, C.CONNECT_AD_ACCOUNTS,
      C.MANAGE_DELIVERABLES,
    ],
    member: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS,
    ],
    viewer: VIEW_ONLY,
  },

  // Managed clients are READ-ONLY by decision (resellers provision for them).
  managed_client: {
    owner:  [...VIEW_ONLY, C.VIEW_BILLING],
    admin:  [...VIEW_ONLY, C.VIEW_BILLING],
    member: VIEW_ONLY,
    viewer: VIEW_ONLY,
  },

  saas_org: {
    owner: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS, C.CONNECT_AD_ACCOUNTS,
      C.MANAGE_TEAM, C.MANAGE_DELIVERABLES, C.VIEW_BILLING, C.MANAGE_BILLING,
    ],
    admin: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS, C.VIEW_AD_METRICS, C.CONNECT_AD_ACCOUNTS,
      C.MANAGE_TEAM, C.MANAGE_DELIVERABLES, C.VIEW_BILLING,
    ],
    member: [
      C.VIEW_DASHBOARD, C.VIEW_MAGNETS, C.PROVISION_MAGNETS, C.MANAGE_MAGNETS,
      C.VIEW_CONVERSATIONS, C.VIEW_STATS,
    ],
    viewer: VIEW_ONLY,
  },
});

/* =========================================================================
   Helpers
   ========================================================================= */

/** Is this acctEntity.class a Mach Five Magnet platform admin (the super-admin tier)? */
export function isPlatformAdmin(entityClassName) {
  const role = getRoleByName(entityClassName);
  return !!role && isAdminRole(role.value); // value >= 50
}

/**
 * Is this a platform admin who may LOOK at everything but CHANGE nothing?
 *
 * DORMANT AS OF 2026-08-13, deliberately. admin_readonly_role was the only class that
 * ever answered yes, and it was removed (never used; see entityClasses.js). Every
 * remaining admin class resolves to the full capability set, so this returns false for
 * every class that exists today, `scope.isReadOnlyAdmin` is always false, and
 * `scope.canAdminWrite()` is currently equivalent to `isPlatformAdmin`.
 *
 * KEPT, rather than deleted with the role, because it is derived from CAPABILITY_MATRIX
 * and not from any role NAME: add an admin class whose matrix entry lacks write
 * capabilities and this — and every gate built on it — starts enforcing immediately,
 * with no code change. That is the difference between dormant machinery and dead code
 * that merely looks alive, and test-admin-authority-split.mjs asserts the dormancy
 * explicitly so the state is documented rather than assumed.
 *
 * The distinction it encodes is not cosmetic. `isPlatformAdmin` alone was being used as
 * the gate for both cross-org VISIBILITY and cross-org WRITES, so a read-only admin
 * passed every write gate in the API while the UI politely hid the buttons.
 */
export function isReadOnlyAdminClass(entityClassName) {
  if (!isPlatformAdmin(entityClassName)) return false;
  const caps = resolveCapabilities({ entityClass: entityClassName });
  return !caps.some(isWriteCapability);
}

/**
 * Resolve the capability set for a request context.
 * @param {object} ctx
 * @param {string} [ctx.entityClass]  acctEntity.class (e.g. 'admin_root_role')
 * @param {string} [ctx.orgChannel]   acctGroup.class of the ACTIVE org
 * @param {string} [ctx.memberRole]   acctGroupMember.member_role in the active org
 * @returns {string[]} capability values (deduped)
 */
export function resolveCapabilities({ entityClass, orgChannel, memberRole } = {}) {
  // Platform admins act across all orgs, with every capability.
  //
  // The `entityClass === 'admin_readonly_role' ? VIEW_ONLY : ALL` special case that used
  // to live here went with the role (2026-08-13). Note where a future restricted admin
  // class belongs: in CAPABILITY_MATRIX or a per-class set, NOT as another name
  // comparison here — a hardcoded name is precisely what made the old restriction
  // invisible to every gate that asked "is this an admin?".
  if (isPlatformAdmin(entityClass)) {
    return [...ALL_CAPABILITIES];
  }
  const byChannel = CAPABILITY_MATRIX[orgChannel];
  const caps = byChannel && byChannel[memberRole];
  return caps ? [...caps] : [];
}

/** Boolean capability check for a context. */
export function can(capability, ctx) {
  return resolveCapabilities(ctx).includes(capability);
}

/**
 * UI mode the client-saas app should render for this context.
 * @returns {'admin'|'reseller'|'self_serve'|'managed'}
 */
export function getUiMode({ entityClass, orgChannel } = {}) {
  if (isPlatformAdmin(entityClass)) return 'admin';
  return ORG_CHANNELS[orgChannel]?.uiMode || 'self_serve';
}

/** Is the channel read-only (managed clients)? */
export function isReadOnlyChannel(orgChannel) {
  return !!ORG_CHANNELS[orgChannel]?.readOnly;
}

/** Can an actor's org role manage a target org role? (higher rank may manage lower) */
export function canManageMemberRole(actorRole, targetRole) {
  const a = MEMBER_ROLES[actorRole]?.rank ?? -1;
  const t = MEMBER_ROLES[targetRole]?.rank ?? Infinity;
  return a >= MEMBER_ROLES.admin.rank && a >= t;
}
